import React, { useMemo } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { dashboardPathForRole, useAuth } from '../../context/AuthContext';

function useForCompaniesCtas() {
  const { user, authLoading, token } = useAuth();

  return useMemo(() => {
    if (authLoading || (token && !user)) {
      return {
        primary: { label: 'Loading…', to: '', loading: true },
        secondary: { label: 'Loading…', to: '', loading: true },
      };
    }
    if (!user) {
      return {
        primary: { label: 'Post Your First Job Free', to: '/register?role=company', loading: false },
        secondary: { label: 'Get Started Free', to: '/register?role=company', loading: false },
      };
    }
    if (user.role === 'company') {
      return {
        primary: { label: 'Go to Dashboard', to: '/company/dashboard', loading: false },
        secondary: { label: 'Go to Dashboard', to: '/company/dashboard', loading: false },
      };
    }
    if (user.role === 'candidate') {
      return {
        primary: { label: 'Create a Company Account', to: '/register?role=company', loading: false },
        secondary: { label: 'Create a Company Account', to: '/register?role=company', loading: false },
      };
    }
    const dash = dashboardPathForRole(user.role);
    return {
      primary: { label: 'Go to Dashboard', to: dash, loading: false },
      secondary: { label: 'Go to Dashboard', to: dash, loading: false },
    };
  }, [user, authLoading, token]);
}

function PrimaryCtaButton({ cta }) {
  if (cta.loading) {
    return (
      <Button type="button" variant="info" size="lg" className="text-white rwm-for-companies__cta-btn btn-lg" disabled aria-busy="true">
        {cta.label}
      </Button>
    );
  }
  return (
    <Button
      as={Link}
      to={cta.to}
      variant="info"
      size="lg"
      className="text-white rwm-for-companies__cta-btn btn-lg"
      data-testid="rwm-for-companies-cta-primary"
    >
      {cta.label}
    </Button>
  );
}

function SecondaryCtaButton({ cta }) {
  if (cta.loading) {
    return (
      <Button
        type="button"
        variant="outline-info"
        size="lg"
        className="rwm-for-companies__cta-btn btn-lg"
        disabled
        aria-busy="true"
      >
        {cta.label}
      </Button>
    );
  }
  return (
    <Button
      as={Link}
      to={cta.to}
      variant="outline-info"
      size="lg"
      className="rwm-for-companies__cta-btn btn-lg"
      data-testid="rwm-for-companies-cta-secondary"
    >
      {cta.label}
    </Button>
  );
}

function ForCompanies() {
  const ctas = useForCompaniesCtas();

  return (
    <div className="rwm-for-companies">
      <section
        className="container-fluid rwm-for-companies__hero rwm-for-companies__section"
        data-testid="rwm-for-companies-hero"
      >
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={10} xl={8}>
              <h1 className="rwm-for-companies__title display-5 fw-bold mb-3">Hire globally. Pay fairly. Respect candidates.</h1>
              <p className="rwm-for-companies__lead lead text-muted mb-4">
                Post remote jobs to a global audience of qualified candidates who expect salary transparency and real
                responses.
              </p>
              <div className="d-flex flex-column flex-md-row justify-content-center align-items-stretch align-items-md-center gap-3 mb-3">
                <PrimaryCtaButton cta={ctas.primary} />
              </div>
              <p className="rwm-for-companies__hero-note small text-muted mb-0">
                First listing free. No credit card required.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="rwm-for-companies__section rwm-for-companies__section--alt" data-testid="rwm-for-companies-values">
        <Container>
          <Row className="g-4">
            <Col md={4}>
              <Card className="rwm-for-companies__value-card h-100 border shadow-sm">
                <Card.Body className="p-4">
                  <div className="rwm-for-companies__value-icon mb-3" aria-hidden="true">
                    💰
                  </div>
                  <Card.Title as="h2" className="h5 fw-bold mb-3">
                    Salary transparency builds trust
                  </Card.Title>
                  <Card.Text className="text-muted mb-0">
                    Candidates know what to expect before they apply. You get serious applicants who won&apos;t drop out
                    over compensation surprises.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="rwm-for-companies__value-card h-100 border shadow-sm">
                <Card.Body className="p-4">
                  <div className="rwm-for-companies__value-icon mb-3" aria-hidden="true">
                    🤝
                  </div>
                  <Card.Title as="h2" className="h5 fw-bold mb-3">
                    Your reputation matters
                  </Card.Title>
                  <Card.Text className="text-muted mb-0">
                    RemoteWorkMarket requires companies to update every application status. No more candidates waiting in
                    silence — and no more ghosting complaints.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="rwm-for-companies__value-card h-100 border shadow-sm">
                <Card.Body className="p-4">
                  <div className="rwm-for-companies__value-icon mb-3" aria-hidden="true">
                    ✅
                  </div>
                  <Card.Title as="h2" className="h5 fw-bold mb-3">
                    Every listing reviewed
                  </Card.Title>
                  <Card.Text className="text-muted mb-0">
                    Every job post is manually approved before going live. This keeps quality high and means your listing
                    reaches candidates who trust the platform.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="rwm-for-companies__section" data-testid="rwm-for-companies-pricing">
        <Container>
          <h2 className="text-center fw-bold mb-2">Simple, transparent pricing</h2>
          <p className="text-center text-muted mb-4">No subscriptions. No hidden fees. Pay per listing.</p>
          <Row className="justify-content-center">
            <Col xs={12} md={8} lg={6}>
              <Card className="rwm-for-companies__pricing-card border shadow">
                <Card.Body className="p-4 p-md-5 text-center">
                  <p className="rwm-for-companies__price display-4 fw-bold mb-1">€149</p>
                  <p className="text-muted mb-4">per job listing · 30-day active duration</p>
                  <ul className="list-unstyled text-start mx-auto rwm-for-companies__pricing-list mb-0">
                    <li className="mb-2">✓ First listing free (one-time per company)</li>
                    <li className="mb-2">✓ Manual review included</li>
                    <li className="mb-2">✓ Salary transparency badge</li>
                    <li className="mb-2">✓ Anti-ghosting enforcement</li>
                    <li className="mb-2">✓ Applications via RWM Flow, email, or external URL</li>
                  </ul>
                </Card.Body>
              </Card>
              <p className="rwm-for-companies__vat-note small text-muted text-center mt-3 mb-0">
                Prices shown ex-VAT. Dutch companies: 21% VAT applies.
                <br />
                EU companies with valid VAT ID: reverse charge. Non-EU: no EU VAT.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="rwm-for-companies__section rwm-for-companies__section--alt" data-testid="rwm-for-companies-how">
        <Container>
          <h2 className="text-center fw-bold mb-4">How it works</h2>
          <Row className="g-4 text-center text-md-start">
            <Col md={4}>
              <div className="rwm-for-companies__step">
                <span className="rwm-for-companies__step-num">1</span>
                <h3 className="h6 fw-bold mt-3 mb-2">Create your company account</h3>
                <p className="text-muted small mb-0">Free, takes 2 minutes</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="rwm-for-companies__step">
                <span className="rwm-for-companies__step-num">2</span>
                <h3 className="h6 fw-bold mt-3 mb-2">Post your first job</h3>
                <p className="text-muted small mb-0">Free trial, no credit card</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="rwm-for-companies__step">
                <span className="rwm-for-companies__step-num">3</span>
                <h3 className="h6 fw-bold mt-3 mb-2">Review applications</h3>
                <p className="text-muted small mb-0">Manage everything from your dashboard</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="rwm-for-companies__section rwm-for-companies__bottom-cta" data-testid="rwm-for-companies-bottom-cta">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <h2 className="fw-bold mb-3">Ready to hire remotely?</h2>
              <p className="text-muted mb-4">
                Join companies that believe in transparency, fairness, and follow-through.
              </p>
              <div className="d-flex flex-column flex-md-row justify-content-center align-items-stretch gap-3">
                <SecondaryCtaButton cta={ctas.secondary} />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}

export default ForCompanies;
